import React, { useCallback, useRef, useState } from "react";
import { FileImage, Loader2, X, UploadCloud } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const MAX_FILES = 4;
const MAX_BYTES = 8 * 1024 * 1024;

export default function ImageUploader({ images, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [previews, setPreviews] = useState({}); // file_id -> objectURL
  const inputRef = useRef(null);

  const upload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    if (images.length + files.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} images.`);
      return;
    }
    setUploading(true);
    const newIds = [];
    const newPreviews = { ...previews };
    for (const f of files) {
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} is larger than 8 MB.`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newIds.push(data.file_id);
        newPreviews[data.file_id] = URL.createObjectURL(f);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Upload failed for ${f.name}`);
      }
    }
    setPreviews(newPreviews);
    onChange([...images, ...newIds]);
    setUploading(false);
  }, [images, onChange, previews]);

  const remove = (id) => {
    onChange(images.filter((x) => x !== id));
    if (previews[id]) {
      URL.revokeObjectURL(previews[id]);
      const np = { ...previews };
      delete np[id];
      setPreviews(np);
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        multiple
        className="hidden"
        onChange={(e) => upload(Array.from(e.target.files || []))}
        data-testid="card-image-input"
      />
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => {
          e.preventDefault();
          upload(Array.from(e.dataTransfer.files || []));
        }}
        className="cursor-pointer py-6"
      >
        {uploading ? (
          <Loader2 className="w-6 h-6 mx-auto text-[#0B3C5D] animate-spin" />
        ) : (
          <UploadCloud className="w-7 h-7 mx-auto text-slate-400" />
        )}
        <p className="mt-2 text-sm text-slate-700">
          {uploading ? "Uploading…" : "Drop card photos here or click to browse"}
        </p>
        <p className="text-[11px] mt-1 text-slate-500">
          Front & back. JPEG/PNG/WebP/HEIC up to 8 MB each. Maximum {MAX_FILES} images.
        </p>
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {images.map((id) => (
            <div key={id} className="relative rounded-xl border border-slate-200 overflow-hidden bg-slate-50 aspect-[1.5/1]" data-testid={`card-image-tile-${id}`}>
              {previews[id] ? (
                // Local preview while session is active
                <img src={previews[id]} alt="Uploaded card" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  <FileImage className="w-6 h-6" />
                </div>
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); remove(id); }}
                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/90 border border-slate-200 flex items-center justify-center text-slate-700 hover:text-red-600"
                aria-label="Remove image"
                data-testid={`card-image-remove-${id}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
